import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, ModalBody, ModalFooter } from 'reactstrap';
import {
  isOnChannel,
  publishOfAssetType,
  type OneOffChannel,
  type SerializedAssetPublish,
} from 'fym-shared/src/music24/oneOffPublish';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import {
  useCreateAssetPublishMutation,
  useDeleteAssetPublishMutation,
  useGetAssetPublishesQuery,
} from '../../../../redux/api/fymAPI';
import type { PublishDialogFile } from './publishDialogStore';
import { publishMessages } from './publishMessages';
import { ONE_OFF_CHANNEL_NAMES, publishBadgeText, publishErrorText } from './publishBadgeText';
import { manualHowTo } from './manualHowTo';
import { trackAssetDownloaded } from '../assetEvents';

interface PublishManualFilesPanelProps {
  accountId: number;
  catalogItemUuid: string;
  channel: OneOffChannel;
  files: PublishDialogFile[];
  onClose: () => void;
}

/**
 * The manual mark of a Kit slot with several files (owner fix 2026-10-10): the four YouTube
 * thumbnails, the two motion artwork files. Each file has its own row with its state, Download and
 * its own mark, so the artist marks only the files that are really on the platform. The record of
 * a row is the newest record of its asset type and output (`publishOfAssetType`), the same rule as
 * the slot badge.
 */
const PublishManualFilesPanelComponent: React.FC<PublishManualFilesPanelProps> = (props) => {
  const { accountId, catalogItemUuid, channel, files, onClose } = props;
  const intl = useIntl();
  const { data } = useGetAssetPublishesQuery({ accountId, catalogItemUuid });
  const publishes = data?.publishes ?? [];
  const [mark, markState] = useCreateAssetPublishMutation();
  const [unmark, unmarkState] = useDeleteAssetPublishMutation();
  const [busyUuid, setBusyUuid] = useState<string | null>(null);
  const error = markState.error ?? unmarkState.error;
  const howTo = manualHowTo(channel);
  const name = ONE_OFF_CHANNEL_NAMES[channel];

  const toggle = async (file: PublishDialogFile, record: SerializedAssetPublish | null): Promise<void> => {
    setBusyUuid(file.kitAssetUuid);
    try {
      if (isOnChannel(record) && record !== null) {
        await unmark({ accountId, publishUuid: record.uuid });
      } else {
        await mark({ accountId, body: { kitAssetUuid: file.kitAssetUuid, channel, timing: 'now' } });
      }
    } finally {
      setBusyUuid(null);
    }
  };

  return (
    <>
      <ModalBody className="d-flex flex-column gap-3" data-testid="publish-manual-files">
        <strong>{intl.formatMessage(publishMessages.filesTitle, { channel: name })}</strong>
        <span>{intl.formatMessage(publishMessages.filesLead, { channel: name })}</span>
        {howTo.steps.length > 0 && (
          // design: assets-rethink b3 `.howto`
          <ol className="mb-0 ps-3 d-flex flex-column gap-1" data-testid="publish-howto">
            {howTo.steps.map((step) => (
              <li key={step.id}>{intl.formatMessage(step, { channel: name })}</li>
            ))}
          </ol>
        )}
        <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
          {files.map((file) => {
            const record = publishOfAssetType(
              publishes,
              { catalogItemUuid, themeId: file.themeId, variant: file.variant },
              channel
            );
            const marked = isOnChannel(record);
            return (
              <li
                key={file.kitAssetUuid}
                className="d-flex flex-wrap align-items-center gap-2"
                data-testid={`publish-file-${file.variant}`}
              >
                <span className="flex-grow-1">
                  <strong className="d-block">{file.label}</strong>
                  <small className="text-muted">{publishBadgeText(intl, channel, record).text}</small>
                </span>
                {file.downloadUrl !== null && (
                  <a
                    className="btn btn-sm btn-outline-secondary"
                    href={file.downloadUrl}
                    download
                    onClick={() => trackAssetDownloaded(undefined, 'manual')}
                  >
                    {intl.formatMessage(publishMessages.actionDownload)}
                  </a>
                )}
                <SMLButton
                  size="sm"
                  color={marked ? 'secondary' : 'primary'}
                  outline={marked}
                  omitCap
                  disabled={busyUuid !== null}
                  onClick={() => toggle(file, record).catch(console.error)}
                >
                  {marked
                    ? intl.formatMessage(publishMessages.actionUndoMark)
                    : intl.formatMessage(howTo.markLabel, howTo.markNamesChannel ? { channel: name } : undefined)}
                </SMLButton>
              </li>
            );
          })}
        </ul>
        {error !== undefined && (
          <Alert color="danger" className="mb-0" data-testid="publish-error">
            {publishErrorText(intl, channel, error)}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <SMLButton color="secondary" outline omitCap onClick={onClose}>
          {intl.formatMessage(publishMessages.actionDone)}
        </SMLButton>
      </ModalFooter>
    </>
  );
};

export const PublishManualFilesPanel = TypedMemo(PublishManualFilesPanelComponent);
